import React, { useEffect, useState } from 'react';
import { browserSupportsWebAuthn, startRegistration } from '@simplewebauthn/browser';
import {
  createPasskeyRegistrationOptions,
  deletePasskey,
  fetchPasskeys,
  verifyPasskeyRegistration,
} from '../api';

function formatDate(value) {
  if (!value) return 'Noch nicht verwendet';
  return new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function friendlyPasskeyError(error) {
  if (error && (error.name === 'NotAllowedError' || error.code === 'ERROR_CEREMONY_ABORTED')) {
    return 'Die Einrichtung wurde abgebrochen oder nicht bestätigt.';
  }
  return (error && error.message) || 'Passkey konnte nicht eingerichtet werden.';
}

export default function PasskeySettings({ onClose, onLogout }) {
  const [data, setData] = useState(null);
  const [deviceName, setDeviceName] = useState('Mein Gerät');
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const supported = browserSupportsWebAuthn();

  async function loadPasskeys() {
    setLoading(true);
    try {
      setData(await fetchPasskeys());
      setError('');
    } catch (loadError) {
      setError(loadError.message || 'Passkeys konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPasskeys();
  }, []);

  async function handleRegister() {
    if (!supported || working) return;
    setWorking(true);
    setError('');
    setSuccess('');
    try {
      const optionsJSON = await createPasskeyRegistrationOptions();
      const response = await startRegistration({ optionsJSON });
      const result = await verifyPasskeyRegistration(response, deviceName);
      setSuccess(`${result.name} wurde eingerichtet. Künftig kannst du dich damit anmelden.`);
      setDeviceName('Mein Gerät');
      await loadPasskeys();
    } catch (registerError) {
      setError(friendlyPasskeyError(registerError));
    } finally {
      setWorking(false);
    }
  }

  async function handleDelete(id) {
    setWorking(true);
    setError('');
    setSuccess('');
    try {
      await deletePasskey(id);
      setConfirmDelete(null);
      setSuccess('Der Passkey wurde entfernt.');
      await loadPasskeys();
    } catch (deleteError) {
      setError(deleteError.message || 'Passkey konnte nicht entfernt werden.');
    } finally {
      setWorking(false);
    }
  }

  const passkeys = (data && data.passkeys) || [];

  return (
    <div className="passkey-overlay" role="presentation">
      <section className="passkey-modal" role="dialog" aria-modal="true" aria-labelledby="passkey-heading">
        <button className="passkey-close" onClick={onClose} aria-label="Schließen">×</button>
        <div className="passkey-kicker">Sicher anmelden</div>
        <h2 id="passkey-heading">Face ID &amp; Passkeys</h2>
        <p className="passkey-intro">
          Richte dieses Gerät einmal ein. Danach kannst du dich nach einem QR-Scan mit Face ID,
          Touch ID, Geräte-PIN oder Windows Hello anmelden.
        </p>

        {!supported && (
          <div className="passkey-message passkey-message-warning">
            Dieser Browser unterstützt Passkeys nicht. Das bisherige Passwort funktioniert weiterhin.
          </div>
        )}
        {data && !data.enabled && (
          <div className="passkey-message passkey-message-warning">
            Passkeys sind auf dem Server noch nicht aktiviert.
          </div>
        )}
        {error && <div className="passkey-message passkey-message-error">{error}</div>}
        {success && <div className="passkey-message passkey-message-success">{success}</div>}

        <div className="passkey-register-card">
          <label htmlFor="passkey-device-name">Name dieses Geräts</label>
          <div className="passkey-register-row">
            <input
              id="passkey-device-name"
              value={deviceName}
              maxLength={80}
              onChange={event => setDeviceName(event.target.value)}
              placeholder="z. B. Andreas’ iPhone"
            />
            <button
              className="app-button app-button-primary"
              onClick={handleRegister}
              disabled={!supported || !data?.enabled || working || !deviceName.trim()}
            >
              {working ? 'Bitte bestätigen …' : 'Dieses Gerät einrichten'}
            </button>
          </div>
          <small>Biometrische Daten verlassen dein Gerät nicht. Das Passwort bleibt als Notfallzugang erhalten.</small>
        </div>

        <div className="passkey-list-heading">
          <h3>Eingerichtete Geräte</h3>
          <span>{passkeys.length}</span>
        </div>

        {loading ? (
          <div className="passkey-empty">Passkeys werden geladen …</div>
        ) : passkeys.length === 0 ? (
          <div className="passkey-empty">Noch kein Gerät eingerichtet.</div>
        ) : (
          <div className="passkey-list">
            {passkeys.map(passkey => (
              <article className="passkey-item" key={passkey.id}>
                <div className="passkey-item-mark" aria-hidden="true">✓</div>
                <div className="passkey-item-copy">
                  <strong>{passkey.name}</strong>
                  <span>Zuletzt: {formatDate(passkey.lastUsedAt)}</span>
                  <small>{passkey.backedUp ? 'Synchronisierter Passkey' : 'Gerätegebundener Passkey'}</small>
                </div>
                {confirmDelete === passkey.id ? (
                  <div className="passkey-delete-confirm">
                    <button onClick={() => handleDelete(passkey.id)} disabled={working}>Entfernen</button>
                    <button onClick={() => setConfirmDelete(null)} disabled={working}>Abbrechen</button>
                  </div>
                ) : (
                  <button className="passkey-remove" onClick={() => setConfirmDelete(passkey.id)}>Entfernen</button>
                )}
              </article>
            ))}
          </div>
        )}

        {passkeys.length > 0 && (
          <button className="passkey-test-login" onClick={onLogout} disabled={working}>
            Abmelden und Face ID testen
          </button>
        )}
      </section>
    </div>
  );
}
